import { useMemo, useState } from "react";
import { MagnifyingGlass, Plus, X } from "@phosphor-icons/react";
import { MemoryList, sortMemories, memorySummary } from "@/components/MemoryList";
import { SEASONS } from "@/data/categories";
import { SPINES } from "@/components/SeasonCover";

const PAPER = {
  backgroundColor: "#FFFDF7",
  backgroundImage: "linear-gradient(90deg, transparent 38px, rgba(224,122,90,0.35) 38px, rgba(224,122,90,0.35) 40px, transparent 40px), repeating-linear-gradient(180deg, transparent 0, transparent 31px, rgba(157,180,200,0.28) 31px, rgba(157,180,200,0.28) 32px)",
};

const Ribbon = ({ color }) => (
  <div aria-hidden data-testid="notebook-ribbon" className="ribbon-drop pointer-events-none absolute -top-1 right-40 z-10 h-20 w-6 drop-shadow-md sm:right-52 sm:h-24 sm:w-7"
    style={{ backgroundColor: color, clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 82%, 0 100%)" }} />
);

const matches = (ev, q) =>
  [ev.title, memorySummary(ev), ev.significant_people, ev.location, ev.description]
    .filter(Boolean).join(" ").toLowerCase().includes(q);

export const SeasonNotebook = ({ season, items, onBack, onSelect, onAdd }) => {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("oldest");
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortMemories(q ? items.filter((ev) => matches(ev, q)) : items, sort);
  }, [items, query, sort]);

  return (
    <section data-testid={`season-notebook-${season.id}`} className="drift-in relative rounded-r-2xl rounded-l-md border border-[#4A4540]/10 border-l-[10px] p-5 pl-12 shadow-[0_14px_36px_rgba(0,0,0,0.08)] sm:p-8 sm:pl-16"
      style={{ ...PAPER, borderLeftColor: SPINES[SEASONS.findIndex((x) => x.id === season.id) % SPINES.length] }}>
      <Ribbon color={SPINES[(SEASONS.findIndex((x) => x.id === season.id) + 2) % SPINES.length]} />
      <div className="relative z-20 flex items-center justify-end gap-3">
        <button data-testid="notebook-close-btn" onClick={onBack} aria-label="Close this season"
          className="flex items-center gap-1.5 rounded-full bg-[#2C2A28] px-4 py-2 text-sm font-semibold text-white shadow-md transition-colors hover:bg-black">
          <X size={20} weight="bold" /> Close
        </button>
      </div>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4 border-b border-[#4A4540]/10 pb-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#A84A32]">{season.note}</p>
          <h2 className="mt-1 font-serif text-3xl text-[#2C2A28] sm:text-4xl">{season.label}</h2>
          <p data-testid="notebook-count" className="mt-1 text-sm text-[#8A7F76]">{items.length} {items.length === 1 ? "memory" : "memories"}</p>
        </div>
        <button data-testid="notebook-add-btn" onClick={() => onAdd(season.id)}
          className="flex items-center gap-1 rounded-full bg-[#A84A32] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#8f3e29]">
          <Plus size={14} weight="bold" /> Add to {season.label}
        </button>
      </div>

      {items.length > 0 && (
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <label className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7F76]" />
            <input data-testid="notebook-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${season.label}`}
              className="w-full rounded-full border border-[#4A4540]/15 bg-white/90 py-2 pl-9 pr-4 text-sm outline-none focus:border-[#A84A32]" />
          </label>
          <select data-testid="notebook-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="rounded-full border border-[#4A4540]/15 bg-white px-4 py-2 text-sm outline-none focus:border-[#A84A32]">
            <option value="oldest">Oldest first</option>
            <option value="newest">Newest first</option>
            <option value="title">Title A–Z</option>
          </select>
        </div>
      )}

      <div className="mt-5">
        {items.length === 0 ? (
          <button data-testid="notebook-empty" onClick={() => onAdd(season.id)}
            className="w-full rounded-lg border border-dashed border-[#4A4540]/20 px-5 py-8 text-sm text-[#8A7F76] hover:border-[#A84A32]">
            This page is still blank. Add your first {season.label} memory.
          </button>
        ) : shown.length === 0 ? (
          <p data-testid="notebook-no-results" className="py-6 text-center text-sm text-[#8A7F76]">No memories match "{query}".</p>
        ) : (
          <MemoryList items={shown} onSelect={onSelect} testId="notebook-list" paper />
        )}
      </div>
    </section>
  );
};
